import { Controller } from 'stimulus';

// Drives the duplication modal: creates the run, posts each of its steps, and
// hands the designer the copy to open when it is done.
export default class extends Controller {
  static targets = [
    'name',
    'option',
    'submit',
    'error',
    'form',
    'progress',
    'caption',
    'row',
    'done',
    'doneMessage',
    'doneIssues',
    'issueTemplate',
    'openProject',
    'title',
    'cancel',
    'toggleAll',
    'nameError',
  ];
  static values = {
    sourceProjectId: Number,
    reloadOnClose: Boolean,
    leaveMessage: String,
    discardMessage: String,
    selectAllLabel: String,
    deselectAllLabel: String,
  };

  #copying = false;
  #cancelling = false;
  // The run whose DELETE failed, kept so Cancel can send it again.
  #pendingDiscard = null;

  connect() {
    this.refreshCount();
    window.addEventListener('beforeunload', this.#unloadHandler);
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.#unloadHandler);
  }

  // Only while a copy is actually running. Nothing is at risk before it starts, and
  // once it has finished the designer is only deciding whether to open the copy --
  // warning them then would be noise.
  #unloadHandler = (event) => {
    if (!this.#copying) {
      return;
    }

    // Safari and Chromium before 119 ignore preventDefault() alone and only show
    // the leave-page prompt when returnValue is set (see billable_form_controller).
    event.preventDefault();
    event.returnValue = 'stay';
    return '';
  };

  // Only the options the project is eligible for; a disabled one is not a
  // choice the designer can make.
  get selectableOptions() {
    return this.optionTargets.filter((input) => !input.disabled);
  }

  toggleAll() {
    const checked = !this.#everythingSelected();

    this.selectableOptions.forEach((input) => {
      input.checked = checked;
    });
    this.refreshCount();
  }

  #everythingSelected() {
    const options = this.selectableOptions;

    return options.length > 0 && options.every((input) => input.checked);
  }

  refreshCount() {
    const selected = this.selectableOptions.filter((input) => input.checked).length;

    this.dispatch('selectionChanged', {
      detail: { current: selected, total: this.selectableOptions.length },
    });
    this.submitTarget.disabled = selected === 0;
    this.toggleAllTarget.textContent = this.#everythingSelected()
      ? this.deselectAllLabelValue
      : this.selectAllLabelValue;
  }

  // Refresh the projects list to show the completed copy.
  afterClose(event) {
    if (!event.target.contains(this.element)) {
      return;
    }

    if (this.reloadOnCloseValue && this.element.dataset.stage === 'done') {
      window.location.reload();
    }
  }

  // Fired by the modal for every close gesture. Vetoing it keeps the modal open.
  beforeClose(event) {
    if (!event.target.contains(this.element)) {
      return;
    }

    if (this.#copying && !window.confirm(this.leaveMessageValue)) {
      event.preventDefault();
    }
  }

  // The step that is already running cannot be called off -- aborting the fetch
  // only abandons our side of it, the server finishes and commits either way -- so
  // the run is stopped between steps and the copy thrown away once it lands.
  cancel() {
    // Already confirmed once; this click is a retry of the delete itself.
    if (this.#pendingDiscard) {
      return this.#discard(this.#pendingDiscard);
    }

    if (this.#cancelling || !window.confirm(this.discardMessageValue)) {
      return;
    }

    this.#cancelling = true;
    this.#allowCancel(false);
    this.captionTarget.textContent = 'Cancelling, finishing the current step first…';
  }

  validateName() {
    const value = this.nameTarget.value;

    this.#toggleNameError(value.length > 0 && value.trim().length === 0);
  }

  async submit(event) {
    event.preventDefault();

    this.#clearError();

    if (!this.#nameGiven()) {
      return;
    }

    this.submitTarget.disabled = true;
    this.#copying = true;
    this.#showProgress();
    this.#startPhase('create');

    const response = await this.#post('/api/df/project_duplications', {
      project_duplication: {
        source_project_id: this.sourceProjectIdValue,
        name: this.nameTarget.value,
        options: this.#selectedOptions(),
      },
    });

    // Nothing was created, so the designer can fix whatever it was and retry.
    if (!response.ok) {
      this.#showForm();
      this.submitTarget.disabled = false;
      this.#showError(await this.#errorMessage(response));
      return;
    }

    const duplication = await response.json();
    this.#finishPhase('create', true);

    const issues = await this.#runSteps(duplication);

    if (this.#cancelling) {
      await this.#discard(duplication);
      return;
    }

    this.#showReady(duplication, issues);
  }

  // Throws the half-made project away, then lets the modal go: #copying is cleared
  // first so the close guard does not ask again about a copy that no longer exists.
  //
  // The server only discards a run that is still in progress. If the last step
  // finished the run before this landed, the copy is whole and is kept, so the
  // designer is handed it instead of being told it was deleted.
  //
  // A failed DELETE leaves the copy on the server with nobody else to remove it,
  // so the modal stays open, still guarding against leaving, and Cancel retries.
  async #discard(duplication) {
    this.#pendingDiscard = null;
    this.#clearError();
    this.#allowCancel(false);

    const response = await this.#request(`/api/df/project_duplications/${duplication.id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      this.#pendingDiscard = duplication;
      this.#allowCancel(true);
      this.captionTarget.textContent = 'The copy could not be deleted.';
      this.#showError(await this.#errorMessage(response));
      return;
    }

    const run = await response.json();

    if (run.status !== 'discarded') {
      this.#showKept(run);
      return;
    }

    this.#copying = false;
    this.element.dispatchEvent(new CustomEvent('turbo-modal:request-close', { bubbles: true }));
  }

  // The copy exists either way, so the run always ends here rather than on a
  // failed-looking panel -- and the designer decides whether to go to it. Nothing
  // navigates on its own, so closing the modal mid-run leaves them where they are.
  //
  // The progress rows are hidden by now, so whatever did not come across is listed
  // here -- otherwise the designer is left guessing whether to start over.
  #showDone(duplication, message, issues) {
    this.#copying = false;
    this.progressTarget.classList.add('hidden');
    this.doneTarget.classList.remove('hidden');
    this.element.dataset.stage = 'done';
    this.#allowEscape(true);

    this.openProjectTarget.href = `/projects/${duplication.project_id}`;
    this.doneMessageTarget.textContent = message;

    this.doneIssuesTarget.replaceChildren(...issues.map((issue) => this.#issueBox(issue)));
    this.doneIssuesTarget.classList.toggle('hidden', issues.length === 0);
  }

  #issueBox({ message, items }) {
    const box = this.issueTemplateTarget.content.firstElementChild.cloneNode(true);
    box.querySelector('[data-issue-heading]').textContent = message;
    box.querySelector('[data-issue-items]').append(...items.map((item) => this.#issueLine(item)));
    return box;
  }

  #issueLine({ name, detail }) {
    const line = document.createElement('li');

    if (name && detail) {
      line.append(Object.assign(document.createElement('strong'), { textContent: `${name}:` }));
      line.append(` ${detail}`);
    } else {
      line.textContent = name ?? detail;
    }

    return line;
  }

  #showReady(duplication, issues) {
    const copiedEverything = issues.length === 0;

    this.#startPhase('finish');
    this.#finishPhase('finish', copiedEverything);
    this.#showDone(
      duplication,
      copiedEverything
        ? `“${duplication.name}” is ready. Open it to add a client and start working.`
        : `“${duplication.name}” is ready, but some parts could not be copied.`,
      issues
    );
  }

  // The run had already finished, so its steps say what came across.
  #showKept(run) {
    const issues = run.steps.map((step) => step.issue).filter(Boolean);
    const copiedEverything = issues.length === 0;

    this.#finishPhase('finish', copiedEverything);
    this.#showDone(
      run,
      copiedEverything
        ? `“${run.name}” had already finished when you cancelled, so it was kept. Open it to add a client and start working.`
        : `“${run.name}” had already finished when you cancelled, so it was kept, but some parts could not be copied.`,
      issues
    );
  }

  // POST /project_duplications creates a step row for every selected option;
  // each one is posted here, one at a time, so each row reports what is
  // actually happening.
  //
  // A step that finished answers with the line describing what it left behind, if
  // anything; one that skipped records still answers 200, so the status alone
  // would report a partial copy as complete.
  async #runSteps(duplication) {
    const issues = [];

    for (const step of duplication.steps) {
      if (this.#cancelling) {
        break;
      }

      // The last step is the one that finishes the run, so once it is in flight
      // there is nothing left to call off.
      this.#allowCancel(step !== duplication.steps.at(-1));
      this.#startPhase(step.kind);
      const response = await this.#post(
        `/api/df/project_duplications/${duplication.id}/${step.kind}`
      );

      const body = await response.json().catch(() => ({}));

      this.#finishPhase(step.kind, response.ok);

      // No issue in the answer means the server never said how it went -- the
      // request failed, or another tab holds the step -- so it may yet finish.
      const issue =
        body.issue ??
        (response.ok
          ? null
          : {
              message: `${this.#row(step.kind).textContent.trim()} may not have finished`,
              items: [],
            });
      if (issue) {
        issues.push(issue);
      }
    }

    return issues;
  }

  // The attribute alone leaves an h-s-button looking clickable; .button-disabled
  // carries the greyed-out look (see buttons-common.scss).
  #allowCancel(allowed) {
    this.cancelTarget.disabled = !allowed;
    this.cancelTarget.classList.toggle('button-disabled', !allowed);
  }

  #showProgress() {
    // A step is part of this run only if the designer picked it. The rest copy
    // nothing, so they report nothing. Create and finish are not options and
    // always run.
    const selected = this.#selectedOptions();

    this.rowTargets.forEach((row) => {
      const kind = row.dataset.phase;
      const runs = !(kind in selected) || selected[kind];

      row.classList.toggle('hidden', !runs);
    });

    this.#showTitle(false);
    this.formTarget.classList.add('hidden');
    this.progressTarget.classList.remove('hidden');
    // Hides the modal's own close X and turns Escape off: Cancel is the way out
    // while copying.
    this.element.dataset.stage = 'progress';
    this.#allowEscape(false);
  }

  #showForm() {
    this.#copying = false;
    // A create that failed while a cancel was pending must not cancel the retry.
    this.#cancelling = false;
    this.#pendingDiscard = null;
    this.#allowCancel(true);
    this.#showTitle(true);
    this.progressTarget.classList.add('hidden');
    this.formTarget.classList.remove('hidden');
    this.element.dataset.stage = 'form';
    this.#allowEscape(true);
  }

  #allowEscape(allowed) {
    this.element
      .closest('[data-controller~="ui--turbo-modal"]')
      .setAttribute('data-ui--turbo-modal-escape-close-value', allowed);
  }

  #showTitle(visible) {
    this.titleTarget.closest('turbo-frame')?.classList.toggle('hidden', !visible);
  }

  #startPhase(phase) {
    const row = this.#row(phase);

    if (!row) {
      return;
    }

    row.dataset.state = 'active';
    this.captionTarget.textContent = `${row.textContent.trim()}…`;
    this.#refreshBar();
  }

  #finishPhase(phase, succeeded) {
    const row = this.#row(phase);

    if (!row) {
      return;
    }

    row.dataset.state = succeeded ? 'done' : 'failed';
    this.#refreshBar();
  }

  // The running phase counts as half, so the bar moves when a phase starts as
  // well as when it lands.
  #refreshBar() {
    const rows = this.#visibleRows();
    const done = rows.filter(
      (row) => row.dataset.state !== 'pending' && row.dataset.state !== 'active'
    ).length;
    const running = rows.filter((row) => row.dataset.state === 'active').length;

    this.dispatch('progress', { detail: { value: ((done + running / 2) / rows.length) * 100 } });
  }

  #visibleRows() {
    return this.rowTargets.filter((row) => !row.classList.contains('hidden'));
  }

  #row(phase) {
    return this.#visibleRows().find((row) => row.dataset.phase === phase);
  }

  #post(url, body) {
    return this.#request(url, { body });
  }

  #request(url, { method = 'POST', body } = {}) {
    return fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
      },
      body: body ? JSON.stringify(body) : undefined,
    }).catch(() => new Response(null, { status: 503 }));
  }

  #selectedOptions() {
    return this.optionTargets.reduce((options, input) => {
      options[input.value] = input.checked && !input.disabled;
      return options;
    }, {});
  }

  async #errorMessage(response) {
    if (response.status === 402) {
      return "You've reached your project limit. Upgrade your plan to duplicate this project.";
    }
    if (response.status === 403) {
      return 'You are not assigned to this project.';
    }
    if (response.status === 429) {
      return "You're duplicating projects quickly. Please wait a minute and try again.";
    }
    if (response.status === 404) {
      return 'This project is no longer available.';
    }

    const body = await response.json().catch(() => ({}));

    if (body.error === 'designs_over_limit') {
      return `This project has ${body.count} designs, over the limit of ${body.limit}. Uncheck Designs to copy the rest.`;
    }
    if (body.error === 'source_not_eligible') {
      return 'This project cannot be duplicated.';
    }
    return 'Something went wrong. Please try again.';
  }

  #nameGiven() {
    if (this.nameTarget.value.trim()) {
      return true;
    }

    this.#toggleNameError(true);
    this.nameTarget.focus();

    return false;
  }

  #toggleNameError(visible) {
    this.nameTarget.classList.toggle('field--error', visible);
    this.nameErrorTarget.classList.toggle('form-error--visible', visible);
  }

  #clearError() {
    this.#toggleNameError(false);

    this.errorTargets.forEach((error) => {
      error.textContent = '';
      error.classList.remove('form-error--visible');
    });
  }

  // .form-error is display:none until the modifier is on it; only the panel on
  // screen has one showing.
  #showError(message) {
    this.errorTargets.forEach((error) => {
      error.textContent = message;
      error.classList.add('form-error--visible');
    });
  }
}
